import { SubheaderText } from '@/utils/global-fonts';
import PropTypes from 'prop-types';
import { useEffect, useRef, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { SUMMARY_CONTEXT } from '@/pages/summary/context-provider';
import { useLocation } from 'react-router-dom';
import Card from '../card/card-component';
import Checkbox from '../react-hook-form-inputs/checkbox/checkbox-component';
import FileUploader from '../react-hook-form-inputs/file-dropzone/file-dropzone-component';

const DisplayUnrealImage = styled.img`
  display: ${({ $active }) => ($active ? 'block' : 'none')};
  max-width: 100%;
  max-height: 100%;
`;

const ExcludeImageContainer = styled.div`
  display: ${({ $excludeImage }) => ($excludeImage ? 'none' : 'block')};
`;

const GreyLine = styled.div`
  display: flex;
  height: 1px;
  background-color: ${({ theme }) => theme.COLOR_GREY5};
`;

const checkboxStyle = { marginTop: 16 };

/**
 * Media form for the Quote Summary page - Media Tab.
 * @param {object} props The component props.
 * @param {string} props.name The name of the input for React Hook Form. (Required)
 * @param {string} props.heading The heading of the component.
 * @param {boolean} props.loading The loading state of the form.
 * @param {Function} props.setValue The setValue function from React Hook Form.
 * @returns {JSX.Element} The rendered Media Form component.
 */
const QuoteMediaImageUploader = ({ name, heading, loading, setValue }) => {
  const { state } = useLocation();
  const { ueImages } = state || {};
  const { watch } = useFormContext();
  const { summary } = useContext(SUMMARY_CONTEXT);

  const userUploadedImage = watch(`${name}`);
  const replaceWithUnrealImage = watch(`include_${name}`);

  const showUnrealImage = replaceWithUnrealImage;
  const imageName = `${name}`;
  const imageRef = useRef(null);

  const excludeUserSelectedImage = () => {
    if (replaceWithUnrealImage) {
      setValue(`${name}`, imageRef.current);
    } else {
      setValue(`${name}`, null);
    }
  };

  const resetImage = () => {
    setValue(`${name}`, null);
    imageRef.current = null;
  };

  const renderUnrealImage = () => {
    if (imageName === 'quote.first_image') {
      if (
        !summary?.product?.override_unreal_images &&
        ueImages?.length > 0 &&
        ueImages[0]?.image
      ) {
        return ueImages[0]?.image;
      }
      return summary?.product_gallery?.[0]?.image;
    }
    if (imageName === 'quote.second_image') {
      if (
        !summary?.product?.override_unreal_images &&
        ueImages?.length > 1 &&
        ueImages[1]?.image
      ) {
        return ueImages[1]?.image;
      }
      return summary?.product_gallery?.[1]?.image;
    }
    if (imageName === 'quote.third_image') {
      if (
        !summary?.product?.override_unreal_images &&
        ueImages?.length > 2 &&
        ueImages[2]?.image
      ) {
        return ueImages[2]?.image;
      }
      return summary?.product_gallery?.[2]?.image;
    }
  };

  useEffect(() => {
    if (userUploadedImage !== null) {
      imageRef.current = userUploadedImage;
    }
  }, [userUploadedImage]);

  useEffect(() => {
    setValue(`include_${name}`, true);
  }, [setValue, name]);

  return (
    <Card margin="8px 0px 8px 0px" borderRadius="4px">
      <SubheaderText>{heading}</SubheaderText>
      <GreyLine />
      <ExcludeImageContainer $excludeImage={showUnrealImage}>
        <FileUploader
          noCrop
          noExtras
          name={`${name}`}
          loading={loading}
          onRemove={resetImage}
        />
      </ExcludeImageContainer>
      <DisplayUnrealImage
        $active={showUnrealImage}
        src={renderUnrealImage()}
        alt={imageName}
      />
      <Checkbox
        label="Include Image"
        style={checkboxStyle}
        disabled={loading || !renderUnrealImage()}
        name={`include_${name}`}
        onClick={excludeUserSelectedImage}
      />
    </Card>
  );
};

QuoteMediaImageUploader.propTypes = {
  name: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  setValue: PropTypes.func.isRequired,
};

QuoteMediaImageUploader.defaultProps = {
  loading: false,
};

export default QuoteMediaImageUploader;
